import axios from "axios";
import {
  getKeycloakUserParticipantIds,
  getParticipantId,
  getParticipantManagementActionKey,
} from "../../hooks/participants/helpers";
import { buildServiceUrl } from "../../utils/urlBuilder";

export function getRealmRoleNames(roles = []) {
  return (Array.isArray(roles) ? roles : [])
    .map((role) => String(role?.name ?? "").trim())
    .filter((role) => role && role !== "default-roles-dsb");
}

export async function loadKeycloakUserRealmRoles(userId, token) {
  const response = await axios.get(
    buildServiceUrl(
      "keycloak",
      `admin/realms/DSB/users/${userId}/role-mappings/realm`,
    ),
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  return getRealmRoleNames(response.data);
}

export async function loadEnabledInternalUsers(token) {
  const response = await axios.get(
    buildServiceUrl("connectorMaster", "keycloak/internal/users"),
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  return (Array.isArray(response.data) ? response.data : [])
    .filter((user) => user?.enabled)
    .filter((user) => String(user?.username ?? "").trim());
}

export function getMatchedApprovalUsers(users, participantId) {
  const normalizedParticipantId = String(participantId ?? "").trim();

  return (Array.isArray(users) ? users : []).filter((user) =>
    getKeycloakUserParticipantIds(user).includes(normalizedParticipantId),
  );
}

export function loadUsersWithRealmRoles(users, token, onRoleLoadError) {
  return Promise.all(
    (Array.isArray(users) ? users : []).map(async (user) => {
      try {
        const roles = await loadKeycloakUserRealmRoles(user.id, token);
        return {
          ...user,
          roles,
        };
      } catch (error) {
        onRoleLoadError?.(user, error);
        return {
          ...user,
          roles: [],
        };
      }
    }),
  );
}

export function getParticipantActionLabel(row, t) {
  switch (getParticipantManagementActionKey(row)) {
    case "reactivate":
      return t("participant.approval.reactivate.action");
    case "activate":
      return t("participant.activate", "Activate");
    case "technicalData":
      return t("participant.technicalData");
    case "deactivate":
      return t("participant.approval.deactivate.action");
    default:
      return t("participant.approval.action");
  }
}

export function getAdditionalParticipantActions(row, t, openTechnicalDataDialog) {
  if (getParticipantManagementActionKey(row) !== "deactivate") return [];

  return [
    {
      label: t("participant.technicalData"),
      onClick: () => openTechnicalDataDialog(row),
    },
  ];
}

export function getParticipantUpdatedAtTime(row) {
  const time = new Date(row?.updated_at).getTime();
  return Number.isFinite(time) ? time : null;
}

export function getOldestUpdatedParticipantId(rows = []) {
  return rows.reduce(
    (oldest, row) => {
      const time = getParticipantUpdatedAtTime(row);
      if (time === null) return oldest;
      if (!oldest || time < oldest.time) {
        return {
          id: getParticipantId(row),
          time,
        };
      }
      return oldest;
    },
    null,
  )?.id;
}

export function shouldHideOldestParticipantActions(row, oldestParticipantId) {
  if (!oldestParticipantId) return false;
  return getParticipantId(row) === oldestParticipantId;
}
